import React, { useEffect, useMemo, useState } from 'react';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { useTheme } from '../helpers/hooks';
import toast from 'react-hot-toast';
import {
  CloseIcon,
  PlusIcon,
  UploadIcon,
  ZoomInIcon,
} from '@iconicicons/react';
import { Edge, Point, SegmentFlowDirection } from '@/types';
import { PointBadge } from '../helpers/PointBadge';
import { VerticalLine } from '../helpers/VerticalLine';
import { useLazyGetPathQuery } from '../services/edgeApi';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import {
  selectAllNodes,
  selectAllPoints,
  selectEdgeLookup,
} from '../map/slice/mapSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { utils, writeFileXLSX } from 'xlsx';
import { mapActions } from '../map/slice/mapSlice';
import { colors, generateColor } from '../utils/colors';
import { useFocusPoint } from '../hooks/useFocusPoint';
import { PathSelectorModal } from '../paths/PathSelectorModal';

const SEPARATOR_HEIGHT = 24;

export const Segmentation = () => {
  const [theme] = useTheme();
  const dispatch = useDispatch();
  const focusPoint = useFocusPoint();
  const [getPath, getPathRequestInfo] = useLazyGetPathQuery();

  const edgeLookup = useSelector(selectEdgeLookup);
  const allNodes = useSelector(selectAllNodes);
  const allPoints = useSelector(selectAllPoints);

  const [points, setPoints] = useState<Point[]>([]);
  const [newPointPosition, setNewPointPosition] = useState<number | null>(null);
  const [path, setPath] = useState<Record<
    string,
    Array<[number, number]>
  > | null>(null);

  const addPoint = (point: Point, index: number) => {
    setPoints((previousValue) => {
      if (
        point.id === previousValue[index]?.id ||
        point.id === previousValue[index - 1]?.id
      ) {
        toast.error('Point already exists in this position');
        return previousValue;
      }
      const nextValue = [...previousValue];
      nextValue.splice(index, 0, point);
      return nextValue;
    });
  };

  const removePoint = (index: number) => {
    setPoints((p) => {
      const n = [...p];
      n.splice(index, 1);
      return n;
    });
  };

  const fetchPath = () => {
    if (points.length < 2) {
      setPath(null);
      return;
    }
    getPath({ nodes: points.map((p) => p.node as number) })
      .unwrap()
      .then((data) => {
        setPath(data);
      })
      .catch((error) => {
        console.error('Error fetching path:', error);
        toast.error('Failed to fetch path');
      });
  };

  const pathData = useMemo(() => {
    return Object.entries(path ?? {}).reduce<
      Array<{
        from: Point;
        to: Point;
        path: [Edge, SegmentFlowDirection][];
      }>
    >((data, [key, nodes]) => {
      const [startNodeId, endNodeId] = key.split('-');
      const startNode = allNodes[startNodeId];
      const endNode = allNodes[endNodeId];
      const startPoint = allPoints[startNode?.points[0]];
      const endPoint = allPoints[endNode?.points[0]];
      const pathEdges = nodes
        .map(
          ([fromNodeId, toNodeId]) =>
            edgeLookup[`${fromNodeId}-${toNodeId}`] || null,
        )
        .filter(Boolean);
      data.push({
        from: startPoint,
        to: endPoint,
        path: pathEdges,
      });
      return data;
    }, []);
  }, [path, allNodes, allPoints, edgeLookup]);

  useEffect(fetchPath, [points]);

  useEffect(() => {
    dispatch(
      mapActions.setPaths(
        Object.values(path ?? {}).flatMap((segments, index, allItems) => {
          const factor = 0.2 + ((index + 1) / allItems.length) * 0.8;
          const color = generateColor(2, 80, factor * 50);
          return segments.map(([startNodeId, endNodeId], index) => {
            const lookupResult = edgeLookup[`${startNodeId}-${endNodeId}`];
            const edge = lookupResult?.[0];
            return {
              nodes: [startNodeId, endNodeId],
              color,
              getProperties: () => {
                return {
                  key: `edge-${edge?.id}`,
                  id: edge?.id,
                };
              },
              options: {
                arrowHeads: index === segments.length - 1,
              },
            };
          });
        }),
      ),
    );
  }, [path]);

  const fields = [
    'Location Name',
    'Location ID',
    'Flow Direction',
    'County',
    'Type',
    'Receipt Zone',
    'Delivery Zone',
    'Seg Nbr',
    'Up/Dn Loc Name',
  ];

  const downloadTable = () => {
    if (points.length < 2) {
      toast.error('At least two points are required to download the path');
      return;
    }

    const data = pathData.flatMap(({ path }, pathIndex, pathElements) => {
      const rows = path.flatMap(
        ([edge, direction], edgeIndex, edgeElements) => {
          const nodePoints = edge.start_points
            .map((pointId) => allPoints[pointId])
            .filter(Boolean);
          const capacities =
            direction === 'FORWARDHAUL'
              ? edge.forwardhaul_capacity_components
              : edge.backhaul_capacity_components.concat(
                  edge.forwardhaul_capacity_components,
                );

          const pointRows = nodePoints.map((point) => [
            point.name,
            point.loc_id,
            point.dir_flo,
            point.loc_cnty,
            point.loc_type_ind,
            point.receipt_zone_name,
            point.delivery_zone_name,
            point.seg_nbr,
            point.up_dn_loc_name,
          ]);
          const capacityRows = capacities.map(([_capacityId, capacityName]) => [
            capacityName,
            '', // Location ID
            '', // Flow Direction
            '', // County
            '', // Type
            '', // Receipt Zone
            '', // Delivery Zone
            '', // Seg Nbr
            '', // Up/Dn Loc Name
          ]);

          if (
            pathIndex === pathElements.length - 1 &&
            edgeIndex === edgeElements.length - 1
          ) {
            const toNodePoints = edge.end_points
              .map((pointId) => allPoints[pointId])
              .filter(Boolean);
            const lastPointRows = toNodePoints.map((point) => [
              point.name,
              point.loc_id,
              point.dir_flo,
              point.loc_cnty,
              point.loc_type_ind,
              point.receipt_zone_name,
              point.delivery_zone_name,
              point.seg_nbr,
              point.up_dn_loc_name,
            ]);
            return pointRows.concat(capacityRows).concat(lastPointRows);
          }
          return pointRows.concat(capacityRows);
        },
      );
      return rows;
    }, []);

    const ws = utils.json_to_sheet([fields, ...data], { skipHeader: true });
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Path');
    writeFileXLSX(
      wb,
      `from-${points[0]?.name.replace(/\s/g, '_')}-to-${points[
        points.length - 1
      ].name.replace(/\s/g, '_')}.xlsx`,
    );
  };

  return (
    <>
      {points.length >= 2 && (
        <span
          role="button"
          onClick={downloadTable}
          className="d-flex align-items-center"
        >
          <UploadIcon className="me-2" />
          Export Path
        </span>
      )}
      <div>
        <h3 className="text-center">Path </h3>
        <div className="d-flex flex-column justify-content-center align-items-center mt-3 gap-2">
          <BadgeDisplay
            role="button"
            onClick={() => setNewPointPosition(0)}
            theme={theme}
          >
            <PlusIcon />
          </BadgeDisplay>
          {points.map((point, index) => (
            <>
              <VerticalLine
                className="mx-3"
                key={`line-pre-${index}`}
                height={SEPARATOR_HEIGHT}
                thickness={3}
                stroke="dashed"
              />
              <PointBadge
                key={point.id.toString()}
                point={point}
                theme={theme}
                extra_text={
                  <>
                    {point.tsp_name}
                    <br />
                    <CloseIcon
                      onClick={(e) => {
                        removePoint(index);
                        e.stopPropagation();
                      }}
                    />
                  </>
                }
              />
              <VerticalLine
                className="mx-3"
                key={`line-post-${index}`}
                height={SEPARATOR_HEIGHT}
                thickness={3}
                stroke="dashed"
              />
              <BadgeDisplay
                key={`add-${index}`}
                role="button"
                onClick={() => setNewPointPosition(index + 1)}
                theme={theme}
              >
                <PlusIcon />
              </BadgeDisplay>
            </>
          ))}
        </div>
      </div>
      <PathSelectorModal
        isOpen={newPointPosition !== null}
        onClose={() => setNewPointPosition(null)}
        lockedTsps={points.length > 0 ? [points[0].tsp_id] : []}
        onSelect={(component) => {
          component.points.forEach((point) => {
            if (point.node) {
              addPoint(point, newPointPosition!);
              setNewPointPosition(null);
            } else {
              toast.error("Can't find paths for unmapped points");
            }
          });
        }}
      />
      <BelowMapSetter>
        <BelowMapTab
          key="segmentation"
          contextKey="segmentation"
          tabName="Path"
        >
          <table className="table" id="segmentation-table">
            <thead
              style={{
                position: 'sticky',
                top: 0,
              }}
            >
              <tr>
                {fields.map((field) => (
                  <th key={field}>{field}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {getPathRequestInfo.isLoading ? (
                <tr>
                  <td colSpan={fields.length} className="text-center">
                    <LoadingIndicator />
                  </td>
                </tr>
              ) : (
                pathData.map(({ from, to, path }, pathIndex, pathElements) => {
                  return (
                    <>
                      <tr key={`from-${from.node}-to-${to.node}`}>
                        <th colSpan={fields.length} className="text-center">
                          From {from.name} to {to.name}
                        </th>
                      </tr>
                      {path.flatMap(
                        ([edge, direction], edgeIndex, edgeElements) => {
                          const nodePoints = edge.start_points
                            .map((pointId) => allPoints[pointId])
                            .filter(Boolean);
                          const capacities =
                            direction === 'FORWARDHAUL'
                              ? edge.forwardhaul_capacity_components
                              : edge.backhaul_capacity_components.concat(
                                  edge.forwardhaul_capacity_components,
                                );
                          const pointRows = nodePoints.map((point) => (
                            <PointRow
                              key={`${from.node}-${to.node}-${point.id}`}
                              point={point}
                              onFocusPoint={focusPoint}
                            />
                          ));
                          const capacityRows = capacities.map(
                            ([capacityId, capacityName]) => (
                              <CapacityRow
                                key={`${from.node}-${to.node}-${capacityId}`}
                                fields={fields}
                                capacityName={capacityName}
                              />
                            ),
                          );
                          const rows = pointRows.concat(capacityRows);
                          if (
                            edgeIndex === edgeElements.length - 1 &&
                            pathIndex === pathElements.length - 1
                          ) {
                            const toNodePoints = edge.end_points
                              .map((pointId) => allPoints[pointId])
                              .filter(Boolean);
                            const lastPointRows = toNodePoints.map((point) => (
                              <PointRow
                                key={`${from.node}-${to.node}-${point.id}`}
                                point={point}
                                onFocusPoint={focusPoint}
                              />
                            ));
                            rows.push(...lastPointRows);
                          }
                          return rows;
                        },
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </BelowMapTab>
      </BelowMapSetter>
    </>
  );
};

const PointRow = ({
  point,
  onFocusPoint,
}: {
  point: Point;
  onFocusPoint: (id: number | string) => any;
}) => {
  return (
    <tr>
      <td>
        <button
          className="btn btn-secondary me-2"
          onClick={() => onFocusPoint(point.id)}
        >
          <ZoomInIcon />
        </button>
        <strong>#{point.id}</strong> {point.name}
      </td>
      <td>{point.loc_id}</td>
      <td>{point.dir_flo}</td>
      <td>{point.loc_cnty}</td>
      <td>{point.loc_type_ind}</td>
      <td>{point.receipt_zone_name}</td>
      <td>{point.delivery_zone_name}</td>
      <td>{point.seg_nbr}</td>
      <td>{point.up_dn_loc_name}</td>
    </tr>
  );
};

const CapacityRow = ({
  fields,
  capacityName,
}: {
  fields: string[];
  capacityName: string;
}) => {
  return (
    <tr>
      <td
        colSpan={fields.length}
        className="text-center text-dark"
        style={{
          backgroundColor: colors.matteYellow,
        }}
      >
        <strong>{capacityName}</strong>
      </td>
    </tr>
  );
};
